import { t } from "@hakili/i18n";
import { Card, Chip, GuardedButton, METADONNEE, TEXTE_SECONDAIRE, TEXTE_TERTIAIRE } from "@hakili/ui";
import { useEffect, useState } from "react";

import type { DoorResult, Inventory, InventoryVehicle } from "../session/useSession.js";
import { type AvailabilityView, availabilityFrom } from "./availability-card.js";
import { stageLabel, stageTone } from "./stages.js";

/**
 * `round F`, `F5`: **« Rendez-vous »**, as `hakili-dealer.jsx:2817-2900` draws
 * it , *« Visites d'acheteurs et passages d'experts »*.
 *
 * ## What a stage can say, and what only a slot can
 *
 * The drawing lists each appointment with its DAY and its HOUR: « mardi 14 h,
 * Awa Traoré », « jeudi 9 h, cabinet Sanogo ». A slot is a fact of its own and
 * `catalog.vehicles` holds none , 03's `WF-DLR-04` and `WF-DLR-05` are the
 * workflows that create them, and neither has a route a dealership can call.
 *
 * What `me/vehicles` DOES say is which cars are at a stage that means somebody
 * is coming: `demande` (a buyer has asked and the dealership has not answered),
 * `visite` (a slot is agreed), `expertise` (the cabinet's round is booked). So
 * this screen is the drawing's two lists with the cars in them and **no day and
 * no hour**, because inventing either would send a dealership to its forecourt
 * on the wrong morning.
 *
 * `gererVisites` is the right that opens the rail entry (06 §6.1.7, `oui` for
 * both rungs). Reading the park needs `gererInventaire`, which both rungs also
 * hold, so nobody who reaches this screen is refused the list underneath it.
 */

/** A buyer is coming, or has asked to (`hakili-dealer.jsx:2823`). */
const BUYER_VISITS: ReadonlySet<string> = new Set(["demande", "visite"]);
/** The cabinet's round (`:2860`). */
const EXPERT_VISITS: ReadonlySet<string> = new Set(["expertise"]);

export interface AppointmentsProps {
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
  readonly naviguer: (to: string) => void;
  /** `T-2c`, `M3 (6)` (WF-DLR-07 step 6): each expertise row reads the dealer's availability door … */
  readonly readAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
  /** … and confirms by it, under the same balance guard as the fiche's. */
  readonly confirmAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
}

/**
 * `T-2c`, `M3 (6)` (`P2-DLW-008`'s Rendez-vous half, WF-DLR-07 step 6): **the per-line confirmation
 * on the Tournées rows**, the same door and the same guard as the fiche's panel , live, or greyed
 * with the door's exact reason (« solde de X FCFA pour une avance de Y FCFA ») and « Recharger mon
 * solde » beside it, « Mise à disposition confirmée » once made available. T-2b's R81 sentence retired.
 */
function AvailabilityLine(props: {
  readonly car: InventoryVehicle;
  readonly initial: AvailabilityView;
  readonly confirm: ((vehicleId: string) => Promise<DoorResult<unknown>>) | undefined;
  readonly naviguer: (to: string) => void;
}): React.JSX.Element | null {
  const { car } = props;
  const [view, setView] = useState<AvailabilityView>(props.initial);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (view.demandId === null) return null;
  if (view.madeAvailableAt !== null) {
    return (
      <p
        className={`mt-1 ${METADONNEE} text-emerald-700`}
        data-testid={`rdv-disposition-${car.stockReference}`}
      >
        {t("dealer.rdv.dispositionConfirmee")}
      </p>
    );
  }
  const press = (): void => {
    if (props.confirm === undefined) return;
    setBusy(true);
    setRefusal(null);
    void props.confirm(car.id).then((answer) => {
      setBusy(false);
      const next = answer.ok ? availabilityFrom(answer.value) : null;
      if (next !== null) setView(next);
      else setRefusal(answer.ok ? t("common.error.connexionEchouee") : answer.message);
    });
  };
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2"
      data-testid={`rdv-disposition-${car.stockReference}`}
    >
      {view.blockedReason === null ? (
        <button
          type="button"
          disabled={busy || props.confirm === undefined}
          onClick={press}
          data-testid={`rdv-confirmer-${car.stockReference}`}
          className="rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
        >
          {t("dealer.controle.confirmer")}
        </button>
      ) : (
        <>
          <GuardedButton
            disabled
            reason={view.blockedReason}
            variant="primary"
            testId={`rdv-confirmer-${car.stockReference}`}
          >
            {t("dealer.controle.confirmer")}
          </GuardedButton>
          <button
            type="button"
            onClick={() => {
              props.naviguer("/solde");
            }}
            data-testid={`rdv-recharger-${car.stockReference}`}
            className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
          >
            {t("dealer.controle.recharger")}
          </button>
        </>
      )}
      {refusal === null ? null : (
        <p
          role="alert"
          data-testid={`rdv-refus-${car.stockReference}`}
          className="w-full text-xs text-rose-600"
        >
          {refusal}
        </p>
      )}
    </div>
  );
}

export function Appointments({
  listVehicles,
  naviguer,
  readAvailability,
  confirmAvailability,
}: AppointmentsProps): React.JSX.Element {
  const [park, setPark] = useState<readonly InventoryVehicle[] | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  /**
   * `T-2c`, `M3 (6)`: the controls the cabinet is coming for, by the dealer's own availability door ,
   * one read per live car. `catalog` does not move a car to `expertise` when Hakili schedules its
   * control (the stage stays `enligne` under the buyer's lock; found at T-2c's park, flagged in the
   * addendum), so the Tournées list reads the demand where it lives rather than the stage.
   */
  const [availability, setAvailability] = useState<Readonly<Record<string, AvailabilityView>>>({});

  useEffect(() => {
    let alive = true;
    void listVehicles().then((answer) => {
      if (!alive) return;
      if (answer.ok) setPark(answer.value.vehicles);
      else setRefus(answer.message);
    });
    return () => {
      alive = false;
    };
  }, [listVehicles]);

  useEffect(() => {
    if (park === null || readAvailability === undefined) return;
    let alive = true;
    const live = park.filter(
      (car) => !["retire", "retireDuCatalogue", "vendueHorsPlateforme"].includes(car.stage),
    );
    void Promise.all(
      live.map(async (car) => {
        const answer = await readAvailability(car.id);
        const view = answer.ok ? availabilityFrom(answer.value) : null;
        return view !== null && view.demandId !== null ? ([car.id, view] as const) : null;
      }),
    ).then((entries) => {
      if (alive) setAvailability(Object.fromEntries(entries.filter((one) => one !== null)));
    });
    return () => {
      alive = false;
    };
  }, [park, readAvailability]);

  const cars = park ?? [];
  const buyers = cars.filter((car) => BUYER_VISITS.has(car.stage));
  const experts = cars.filter((car) => EXPERT_VISITS.has(car.stage) || car.id in availability);

  const section = (
    key: string,
    heading: string,
    empty: string,
    rows: readonly InventoryVehicle[],
  ): React.JSX.Element => (
    // `round F5`, to the pair: the heading stands ABOVE the card, as the
    // drawing puts it (`hakili-dealer.jsx:2820`, `:2858`). Inside, the two
    // sections read as one long card with bold lines in it; outside, each
    // heading names the block under it and the eye finds the second list.
    <div className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{heading}</h2>
      <Card>
        <ul className="divide-y divide-gray-100" data-testid={`rdv-${key}`}>
          {rows.map((car) => (
            <li key={car.id} className="flex items-center gap-4 py-3">
              <button
                type="button"
                onClick={() => {
                  naviguer(`/vehicules/${car.id}`);
                }}
                className="min-w-0 flex-1 text-left"
                data-testid={`rdv-ligne-${car.stockReference}`}
              >
                <p className="text-sm font-medium text-gray-900">{car.title}</p>
                <p className={`tabular-nums ${METADONNEE} ${TEXTE_SECONDAIRE}`}>{car.stockReference}</p>
              </button>
              <Chip tone={stageTone(car.stage)}>{stageLabel(car.stage)}</Chip>
              {((): React.JSX.Element | null => {
                const view = key === "experts" ? availability[car.id] : undefined;
                return view === undefined ? null : (
                  <AvailabilityLine
                    car={car}
                    initial={view}
                    confirm={confirmAvailability}
                    naviguer={naviguer}
                  />
                );
              })()}
            </li>
          ))}
          {park !== null && rows.length === 0 ? (
            <li className="py-3 text-sm text-gray-500" data-testid={`rdv-${key}-vide`}>
              {empty}
            </li>
          ) : null}
        </ul>
      </Card>
    </div>
  );

  return (
    <div className="flex flex-col gap-6" data-testid="rdv">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="rdv-titre">
          {t("dealer.rdv.titre")}
        </h1>
        <p className={`mt-1.5 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.rdv.resume")}</p>
      </div>

      {refus === null ? null : (
        <p role="alert" className="text-sm text-rose-700" data-testid="rdv-refus">
          {refus}
        </p>
      )}

      {section("acheteurs", t("dealer.rdv.acheteurs"), t("dealer.rdv.acheteursVide"), buyers)}
      {section("experts", t("dealer.rdv.experts"), t("dealer.rdv.expertsVide"), experts)}

      {/**
       * `R81`. The drawing carries a day and an hour on every line and a
       * « Confirmer le créneau » beside it. A slot is its own fact; nothing in
       * `catalog` holds one, and `gererVisites` , the right that opened this
       * screen , has no route to exercise. So the screen lists what a stage
       * can justify and says the rest in words.
       */}
      <p className={`${METADONNEE} ${TEXTE_TERTIAIRE}`} data-testid="rdv-en-attente">
        {t("dealer.rdv.enAttente")}
      </p>
    </div>
  );
}
